import type { ReactNode } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useLiveBookStatus } from '@/hooks/useDemoDataAccess'
import { httpStatus } from '@/components/deal/dossier/httpStatus'
import type { PlatformModuleId } from '@/types'

/**
 * Holds a deal-list screen until the live book has actually answered.
 *
 * Every screen fed by `useAntarcticaDeals` received `[]` while GET /loans was
 * in flight or after it failed, and rendered that as an empty pipeline: "No
 * deals available … add a deal from Intake", "SCREENING QUEUE 0", "CLOSING
 * PIPELINE 0 deals". Loading and failure are not "no deals", so this renders
 * them as what they are and only lets the screen draw once the list is real.
 * Demo modules pass straight through.
 */
export default function LiveBookGate({
  moduleId,
  children,
}: {
  moduleId: PlatformModuleId
  children: ReactNode
}) {
  const status = useLiveBookStatus(moduleId)

  if (status.isLoading) {
    return (
      <div role="status" className="flex h-full w-full items-center justify-center p-8">
        <div className="flex items-center gap-2 text-[12px] text-stone-400">
          <Loader2 size={14} className="animate-spin text-amber-300" />
          Loading the deal list…
        </div>
      </div>
    )
  }

  if (status.isError) {
    const code = httpStatus(status.error)
    return (
      <div role="alert" className="flex h-full w-full items-center justify-center p-8">
        <div className="max-w-md rounded-lg border border-red-400/20 bg-black/30 px-6 py-5 text-center">
          <AlertTriangle size={18} className="mx-auto text-red-300" />
          <p className="mt-3 text-[13px] font-medium text-stone-200">The deal list could not be loaded</p>
          <p className="mt-1.5 text-[11px] leading-relaxed text-stone-500">
            {code ? `The server answered HTTP ${code}. ` : 'The server could not be reached. '}
            Nothing on this screen is shown until the list loads, so an outage is never read as an
            empty pipeline. Retrying automatically every 15 seconds.
          </p>
          <button
            type="button"
            onClick={() => void status.refetch()}
            className="mt-4 rounded-md border border-amber-400/25 bg-amber-400/[0.08] px-3 py-1.5 text-[11px] font-medium text-amber-200 transition-colors hover:border-amber-400/40"
          >
            Retry now
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
